import { Flex, Grid, Heading, Stack } from '@hover/blueprint';
import React from 'react';
import { useHistory } from 'react-router';
import type { InferType } from 'yup';

import { makePhoneNumberValidationSchema } from 'src/modules/onboarding/validations/makeValidationSchemas';
import {
  CheckboxField,
  Form as SharedForm,
  TextField,
} from 'src/shared/components/forms';
import {
  ConsentPolicyKinds,
  useGetConsentPoliciesQuery,
  useUpdateUserMutation,
} from 'src/shared/graphql/generated';
import { useEffectOnMount, useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed, pageViewed } from 'src/shared/telemetry';

import { useOnboardingError } from '../hooks/useOnboardingError';

import { OnboardingFormFooter } from './OnboardingFormFooter';

export type UserPhoneFormData = InferType<
  ReturnType<typeof makePhoneNumberValidationSchema>
>;

interface NameFormData {
  phoneNumber: string | undefined;
  enableSMS: boolean;
}
const UserPhoneFormFields = () => {
  const t = useTranslate(TranslationNamespace.SHARED);

  return (
    <Stack
      alignItems="start"
      direction={{ base: 'column', tablet: 'row' }}
      flex={1}
      flexWrap="wrap"
      justifyContent={{ base: 'start', tablet: 'space-around' }}
      marginTop={300}
      spacing={0}
      width={1}
    >
      <Heading maxW={800} size={{ base: 700, tablet: 800 }}>
        {t('onboarding.enterYourPhoneNumber')}
      </Heading>
      <Flex alignItems="stretch" flexDir="column">
        <TextField
          name="phoneNumber"
          placeholder={t('onboarding.enterPhoneNumber')}
          type="tel"
          width={1}
        />
        <CheckboxField
          ariaLabel="enable SMS"
          maxW={800}
          ml={300}
          name="enableSMS"
        >
          {t('onboarding.iOptInToReceive')}
        </CheckboxField>
      </Flex>
    </Stack>
  );
};

export const UserPhone = () => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const history = useHistory();
  const { logError } = useOnboardingError();
  const [updateUser] = useUpdateUserMutation();
  const validationSchema = makePhoneNumberValidationSchema(t);
  const initialValues = { phoneNumber: '', enableSMS: false };

  useEffectOnMount(() => {
    pageViewed({
      page_or_screen_name: 'Onboarding-phone_input',
      feature: 'onboarding',
    });
  });

  const { data: consentPolicies } = useGetConsentPoliciesQuery({
    variables: { kinds: [ConsentPolicyKinds.Sms] },
  });

  const onSubmit = async (formData: NameFormData) => {
    buttonPressed({
      feature: 'onboarding',
      page_or_screen_name: 'Onboarding-phone_input',
      button_text: 'Next',
    });

    const { phoneNumber } = formData;
    if (!phoneNumber) return;

    const policy = consentPolicies?.consentPolicies?.[0];
    if (!policy) return;

    try {
      await updateUser({
        variables: {
          consents: [
            {
              policyId: policy.id,
              hasConsent: formData.enableSMS,
              source: 'web-signup-onboarding',
            },
          ],
          mobilePhone: phoneNumber,
        },
      });
    } catch (e) {
      logError(e as Error);
    } finally {
      history.push(`${Routes.SIGN_UP_ONBOARDING.path}/download`);
    }
  };

  return (
    <Grid alignItems="stretch" flex={1} width={1}>
      <SharedForm<UserPhoneFormData>
        display="flex"
        height="100%"
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <UserPhoneFormFields />
        <OnboardingFormFooter hideForMobile={false} />
      </SharedForm>
    </Grid>
  );
};
